/* 페이지 — 위쪽 막대(몇 번째 · 복제 · 삭제 · 확대/축소) + 아래쪽 모든 페이지 썸네일(눌러서 이동 · 끌어서 순서 · 복제·삭제) + ＋ 템플릿 페이지 추가 */
import { useEffect, useRef, useState } from 'react';
import { Button, Temp_TileButton } from '@indipia/ui-core';
import { previewPages } from '../model/templates';
import { PAGE_KIND_LABEL } from '../model/templates';
import { clearSamplePhotos } from '../model/samples';
import { pageId, type Page } from '../model/types';
import {
  clonePage, currentTemplate, deletePageAt, duplicatePageAt, ed, insertPageAfter, movePage, showPage, toast, useEd,
} from '../store/editor';
import { PageThumb, PageThumbMemo } from './render';
import { IcClose } from './icons';
import { cx, iconBtn, popHead, popover } from './ui';

const APM = 'flex flex-col items-center gap-xs rounded-md p-02 hover:bg-group-subtle [&_.page-thumb]:rounded-sm [&_.page-thumb]:border [&_.page-thumb]:border-border [&>span]:text-caption [&>span]:font-semibold';
const PAGE_WHITE = '#FFFFFF'; // dq-ignore: 문서 페이지 기본 배경

export function PageRail() {
  const e = useEd();
  const zoom = (d: number) => { ed.zoom = Math.round(Math.max(0.4, Math.min(1.2, ed.zoom + d)) * 10) / 10; ed.bump(); };
  return (
    <div className="flex shrink-0 items-center gap-sm overflow-x-auto border-b border-border bg-surface-raised px-05 py-03">
      <span className="whitespace-nowrap text-caption font-semibold text-text-secondary" aria-live="polite">{e.cur + 1} / {e.pages.length} 페이지</span>
      <span className="flex-1" />
      <Button size="sm" variant="etc" title="이 페이지 복제 (요소 선택 없이 Ctrl+C → Ctrl+V)" onClick={() => duplicatePageAt(ed.cur)}>페이지 복제</Button>
      <Button size="sm" variant="etc" onClick={() => deletePageAt(ed.cur)}>이 페이지 삭제</Button>
      <div className="flex items-center gap-xs rounded-full bg-surface px-02 [&>span]:min-w-08 [&>span]:text-center [&>span]:text-caption [&>span]:tabular-nums [&>span]:text-text-secondary" role="group" aria-label="확대·축소">
        <Button size="sm" variant="ghost" className={iconBtn} aria-label="축소" disabled={e.zoom <= 0.4} onClick={() => zoom(-0.1)}>−</Button>
        <span>{Math.round(e.zoom * 100)}%</span>
        <Button size="sm" variant="ghost" className={iconBtn} aria-label="확대" disabled={e.zoom >= 1.2} onClick={() => zoom(0.1)}>＋</Button>
      </div>
    </div>
  );
}

/** 문서를 직접 고치므로 — 1.5초마다 바뀐 게 있으면 썸네일을 다시 그린다 */
function useThumbTick() {
  const [tick, setTick] = useState(0);
  const last = useRef(-1);
  useEffect(() => {
    const t = setInterval(() => { if (ed.rev !== last.current) { last.current = ed.rev; setTick((x) => x + 1); } }, 1500);
    return () => clearInterval(t);
  }, []);
  return tick;
}

export function PageStrip() {
  const e = useEd();
  const tick = useThumbTick();
  const [drag, setDrag] = useState<number | null>(null);
  const [drop, setDrop] = useState<{ i: number; before: boolean } | null>(null);
  const [menu, setMenu] = useState<{ left: number; bottom: number } | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stripRef.current?.querySelector('[data-current]')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [e.cur, e.pages.length]);
  return (
    <div className="flex shrink-0 items-start gap-sm overflow-x-auto border-t border-border bg-surface-raised px-05 pb-03 pt-04" ref={stripRef} role="list" aria-label="페이지 목록">
      {e.pages.map((p, i) => (
        <div key={p.id} role="listitem" draggable title={`${i + 1}페이지 — 끌어서 순서 바꾸기`}
          data-current={i === e.cur || undefined} className={cx('group/item relative shrink-0', drag === i && 'opacity-40')}
          onDragStart={(ev) => { setDrag(i); ev.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(ev) => {
            if (drag === null) return;
            ev.preventDefault();
            const r = ev.currentTarget.getBoundingClientRect();
            setDrop({ i, before: ev.clientX < r.left + r.width / 2 });
          }}
          onDrop={(ev) => {
            ev.preventDefault();
            if (drag === null) return;
            const r = ev.currentTarget.getBoundingClientRect();
            movePage(drag, i + (ev.clientX < r.left + r.width / 2 ? 0 : 1));
            setDrag(null); setDrop(null);
          }}
          onDragEnd={() => { setDrag(null); setDrop(null); }}>
          {drop?.i === i && <span aria-hidden className={cx('absolute top-0 h-12 w-[var(--border-width-thick)] bg-group', drop.before ? '-left-02' : '-right-02')} />}
          <Temp_TileButton plain className="flex flex-col items-center gap-xs" selected={i === e.cur} aria-label={`${i + 1}페이지로 이동`}
            onClick={() => { if (i !== ed.cur) { ed.selectedId = null; showPage(i); } }}>
            <PageThumbMemo page={p} width={72} tick={tick} className="rounded-sm border border-border shadow-[var(--elevation-1)] group-hover:border-[var(--neutral-200)] group-aria-pressed:border-group group-aria-pressed:shadow-[0_0_0_2px_var(--gt-primary)]" />
            <span className="text-caption font-semibold text-text-secondary group-aria-pressed:text-group">{i + 1}</span>
          </Temp_TileButton>
          <div className="absolute right-01 top-01 flex gap-xs opacity-0 group-focus-within/item:opacity-100 group-hover/item:opacity-100 [&_.btn]:size-06 [&_.btn]:bg-surface-raised [&_.btn]:shadow-[var(--elevation-1)]">
            <Button size="sm" variant="ghost" className={iconBtn} aria-label={`${i + 1}페이지 복제`} title="복제" onClick={() => duplicatePageAt(i)}>⧉</Button>
            <Button size="sm" variant="ghost" className={iconBtn} aria-label={`${i + 1}페이지 삭제`} title="삭제" onClick={() => deletePageAt(i)}><IcClose size={16} /></Button>
          </div>
        </div>
      ))}
      <Temp_TileButton bordered data-add-page className="flex h-12 w-[calc(var(--spacing-10)+var(--spacing-03))] shrink-0 flex-col items-center justify-center gap-xs rounded-sm border-dashed bg-surface text-group hover:bg-group-subtle [&>b]:text-heading-md [&>b]:leading-none [&>span]:text-caption [&>span]:font-semibold" title="페이지 추가 — 템플릿 페이지 모양 그대로" aria-haspopup="menu" aria-expanded={!!menu}
        onClick={(ev) => {
          if (menu) { setMenu(null); return; }
          const r = ev.currentTarget.getBoundingClientRect();
          setMenu({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 10 });
        }}>
        <b aria-hidden>＋</b><span>페이지 추가</span>
      </Temp_TileButton>
      {menu && <AddPageMenu at={menu} onClose={() => setMenu(null)} />}
    </div>
  );
}

function AddPageMenu({ at, onClose }: { at: { left: number; bottom: number }; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(at.left);
  useEffect(() => {
    const w = ref.current?.offsetWidth || 0;
    setLeft(Math.max(8, Math.min(window.innerWidth - w - 8, at.left - w / 2)));
    const down = (e: MouseEvent) => { const t = e.target as HTMLElement; if (ref.current && !ref.current.contains(t) && !t.closest('[data-add-page]')) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [at.left, onClose]);
  const tpl = currentTemplate();
  const seen = new Set<string>(), choices: { label: string; page: Page }[] = [];
  previewPages(tpl).forEach((p) => {
    const k = p.kind || 'page';
    if (seen.has(k)) return;
    seen.add(k);
    choices.push({ label: PAGE_KIND_LABEL[k] || '페이지', page: p });
  });
  const pick = (fn: () => void) => { onClose(); fn(); };
  return (
    <div ref={ref} className={cx(popover, 'max-w-[min(var(--layout-container-md),96vw)]')} role="menu" aria-label="페이지 추가" style={{ left, bottom: at.bottom }}>
      <div className={cx(popHead, 'flex-col items-start gap-xs')}><b>페이지 추가</b><span className="text-caption text-text-secondary">'{tpl.name}' 템플릿의 페이지 모양 그대로 지금 페이지 뒤에 넣어요</span></div>
      <div className="flex flex-wrap gap-sm">
        {choices.map((c) => (
          <Temp_TileButton key={c.label} role="menuitem" className={APM} onClick={() => pick(() => { insertPageAfter(clearSamplePhotos(clonePage(c.page)), ed.cur); toast(`'${c.label}' 페이지를 추가했어요 — 회색 사진 칸을 누르고 사진 탭에서 사진을 고르세요`); })}>
            <PageThumb page={c.page} width={96} /><span>{c.label}</span>
          </Temp_TileButton>
        ))}
        <Temp_TileButton role="menuitem" className={APM} onClick={() => pick(() => duplicatePageAt(ed.cur))}><PageThumb page={ed.pages[ed.cur]} width={96} /><span>지금 페이지 복제</span></Temp_TileButton>
        <Temp_TileButton role="menuitem" className={APM} onClick={() => pick(() => insertPageAfter({ id: pageId(), bg: ed.pages[ed.cur]?.bg || PAGE_WHITE, elements: [] }, ed.cur))}>
          <span className="block h-[calc(var(--spacing-12)+var(--spacing-07))] w-12 rounded-sm border border-dashed border-border bg-[var(--gray-0)]" aria-hidden /><span>빈 페이지</span>
        </Temp_TileButton>
      </div>
    </div>
  );
}
